export const ACTIVE_UPDATE_JOB_STATUSES = ["queued", "running"];

// Contrato compartilhado (Task 14, pipeline/BE/FE em paralelo, 02/09): 429 do Sienge vira este
// código no job (`error_code`) e no corpo do POST (`{ code, message, retry_after }`); a
// construtora fica com `sienge_quota_blocked_until` (Date UTC) até a próxima renovação da cota.
export const SIENGE_QUOTA_ERROR_CODE = "sienge_quota_exceeded";

// Task 17B: o data-pipeline marca como `failed`/`service_restarted` os `update_jobs` que
// ficaram em `queued`/`running` quando o serviço reinicia (deploy, restart) sem que o pipeline
// chegue a fechá-los sozinho. O usuário não fez nada de errado -- só precisa disparar de novo.
export const SERVICE_RESTARTED_ERROR_CODE = "service_restarted";
export const SERVICE_RESTARTED_MESSAGE = "A atualização foi interrompida por uma reinicialização do serviço. Dispare novamente.";

// Contrato compartilhado (21/09, pipeline/BE/FE): o ERP respondeu 401/403 numa rota de
// extração. As credenciais valem — quem não tem permissão na rota é o usuário da
// integração, e só o administrador da API do ERP libera. Repetir a atualização não muda
// nada, então a mensagem nomeia as rotas (`job.extraction.permission_denied`) e o ERP em
// vez do genérico "não foi possível concluir a atualização".
export const PERMISSION_DENIED_ERROR_CODE = "permission_denied";

const ERP_LABELS = { SIENGE: "Sienge", MEGA: "MEGA" };

// O nome da rota é o endpoint do ERP ("building-cost-estimations/cost-estimate-resources"):
// diz tudo para quem debuga o pipeline e nada para quem olha a obra. Aqui ele vira a
// informação que falta na tela, que é o que o usuário precisa para saber o que não está
// disponível. Rota sem tradução aparece como veio — melhor um nome técnico do que omitir
// que algo faltou.
export const EXTRACTION_ROUTE_LABELS = {
  // Sienge
  "building-cost-estimations/items": "Orçamento (itens)",
  "building-cost-estimations/cost-estimate-resources": "Insumos do orçamento",
  "building-resources": "Recursos da obra",
  income: "Contas a receber",
  "building-projects": "Avanço físico-financeiro",
  "bank-movements": "Movimento bancário",
  outcome: "Contas a pagar",
  "supply-contracts": "Contratos de suprimentos",
  "supply-contracts/items": "Itens dos contratos",
  // MEGA
  "auth/signIn": "Conexão com o MEGA",
  projects: "Planilha orçamentária",
  budgetCompositions: "Composições do orçamento",
  measurements: "Medições",
  financial: "Financeiro",
};

export const extractionRouteLabel = (route) => EXTRACTION_ROUTE_LABELS[route] ?? route;

const routeList = (value) => (
  Array.isArray(value) ? value.filter((route) => typeof route === "string" && route) : []
);

const deniedRoutes = (job) => routeList(job?.extraction?.permission_denied);

// O que não entrou nesta execução, com o motivo — é isto que responde "por que este número
// não está disponível?". Uma rota negada que manteve o download anterior é diferente de uma
// que não tinha nada para manter: na primeira o dado está velho, na segunda não existe.
export const unprocessedSources = (job) => {
  const denied = new Set(deniedRoutes(job));
  const degraded = new Set(routeList(job?.extraction?.degraded));
  const failed = routeList(job?.extraction?.failed);
  const routes = [...new Set([...denied, ...degraded, ...failed])];
  return routes.map((route) => {
    const stale = degraded.has(route);
    // "Fonte indisponível" prometia demais: a rota também entra aqui quando o ERP
    // respondeu e mandou um dado que o pipeline não conseguiu ler (22/09:
    // `agrupamentos[48].valor_pago` não numérico no MEGA). Quem lê a tela precisa
    // saber que a informação não veio, sem ser mandado atrás da causa errada.
    const cause = denied.has(route)
      ? `Sem permissão na API ${erpLabel(job)}`
      : "Não foi possível obter esta informação do ERP";
    return {
      route,
      label: extractionRouteLabel(route),
      stale,
      reason: stale
        ? `${cause}. Mantido o dado da última atualização.`
        : `${cause}. Esta informação não foi atualizada.`,
    };
  });
};

const erpLabel = (job) => ERP_LABELS[String(job?.extraction?.erp || "").toUpperCase()] ?? "do ERP";

const routesSentence = (job) => {
  const routes = deniedRoutes(job).map(extractionRouteLabel);
  return routes.length > 0 ? ` Não atualizado: ${routes.join(", ")}.` : "";
};

export const permissionDeniedMessage = (job) => (
  `O ERP negou o acesso a rotas necessárias para esta atualização.${routesSentence(job)} `
  + `Peça a liberação ao administrador da API ${erpLabel(job)} e dispare novamente.`
);

// Uma atualização pode terminar com sucesso e ainda assim ter rotas negadas: as demais
// foram baixadas e as negadas mantiveram o último download. Sem esta ressalva o usuário
// lê um número desatualizado como recém-atualizado.
export const partialExtractionMessage = (job) => {
  if (deniedRoutes(job).length === 0) return "";
  const { succeeded, total } = job?.extraction ?? {};
  const counted = Number.isFinite(succeeded) && Number.isFinite(total)
    ? `${succeeded} de ${total} rotas foram atualizadas. `
    : "";
  return `${counted}${permissionDeniedMessage(job)} `
    + "Até lá, esses dados permanecem como estavam na última atualização bem-sucedida.";
};

// Em stg o pipeline não extrai: reprocessa a extração que prod já fez da obra (change 11) e
// grava no job `extraction.mode = "prod_reprocess"` e a data dessa extração. Quem olha a tela
// precisa saber de quando são os dados. Fora de stg o campo não vem e não há rótulo.
export const PROD_REPROCESS_MODE = "prod_reprocess";

export const reprocessedExtractionLabel = (job) => {
  if (job?.extraction?.mode !== PROD_REPROCESS_MODE) return "";
  const date = parseSiengeQuotaBlockedUntil(job.extraction.source_extracted_at);
  if (!date) return "Reprocessou a extração de prod";
  const day = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `Reprocessou a extração de prod de ${day} às ${time}`;
};

const UPDATE_TYPES_BY_ERP = {
  // "disbursement" saiu em 2026-10-07: o desembolso é o Desembolso Ampli, sem atualização própria.
  SIENGE: [
    "complete",
    "trend",
    "financial",
    "purchase_orders",
    "stock",
    "prevision",
  ],
  MEGA: ["complete", "financial", "prevision"],
};
const UPDATE_JOB_TYPES = new Set([
  "complete",
  "disbursement",
  "trend",
  "financial",
  "purchase_orders",
  "stock",
  "prevision",
]);
const UPDATE_CAPABILITY_CONDITIONS = new Set(["available", "read_only", "unavailable"]);

const parseBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const request = async (fetchFn, url, options) => {
  try {
    const response = await fetchFn(url, options);
    return {
      ok: response.ok,
      status: response.status,
      body: await parseBody(response),
    };
  } catch {
    return { ok: false, status: 0, body: null };
  }
};

export const requestUpdateJob = ({
  fetchFn,
  serverUrl,
  companyId,
  buildingId,
  type,
  period,
}) => request(fetchFn, `${serverUrl}/project/updateJob`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    company_id: companyId,
    building_id: buildingId,
    type,
    // `period: null` explícito pede o histórico completo de Pedidos e Contratos (só MEGA) --
    // diferente de `period` simplesmente ausente (outros tipos de atualização, que não usam
    // período). `!== undefined` preserva o `null` na wire; `period &&` derrubaria os dois casos
    // no mesmo `{}`.
    ...(period !== undefined ? { period } : {}),
  }),
});

export const loadUpdateJob = ({ fetchFn, serverUrl, buildingId }) => request(
  fetchFn,
  `${serverUrl}/project/updateJob?building_id=${encodeURIComponent(buildingId)}`,
  { method: "GET" },
);

export const isUpdateJobActive = (job) => ACTIVE_UPDATE_JOB_STATUSES.includes(job?.status);

export const canUseUpdateType = (erp, type) => (
  UPDATE_TYPES_BY_ERP[String(erp || "").toUpperCase()]?.includes(type) ?? false
);

export const resolveProjectUpdateTypes = (project, erp) => {
  const contract = project?.updateCapabilities;
  if (contract !== undefined && contract !== null) {
    if (
      typeof contract !== "object"
      || Array.isArray(contract)
      || contract.version !== 1
      || !UPDATE_CAPABILITY_CONDITIONS.has(contract.condition)
      || !Array.isArray(contract.types)
    ) return [];
    if (contract.condition !== "available") return [];
    return [...new Set(contract.types.filter((type) => UPDATE_JOB_TYPES.has(type)))];
  }
  return [...(UPDATE_TYPES_BY_ERP[String(erp || "").toUpperCase()] ?? [])];
};

// Só a data (sem hora) usa timeZone: "UTC" no resto do arquivo de datas (`formatDate.js`) porque
// aquelas datas são "dia puro", sem componente de hora real. `sienge_quota_blocked_until` é um
// instante de verdade (o próximo 00:00 em America/Sao_Paulo, gravado em UTC) — o usuário precisa
// ver a hora convertida para o fuso do navegador, não UTC cru.
export const parseSiengeQuotaBlockedUntil = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isSiengeQuotaBlocked = (value, now = new Date()) => {
  const date = parseSiengeQuotaBlockedUntil(value);
  return !!date && date.getTime() > now.getTime();
};

export const formatSiengeQuotaMessage = (value) => {
  const date = parseSiengeQuotaBlockedUntil(value);
  const base = "Cota diária da API Sienge desta construtora foi atingida.";
  if (!date) return base;
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const day = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${base} Nova atualização disponível a partir de ${time} de ${day}.`;
};

// `quotaBlockedUntil`: o BE (`serializeUpdateJob`) NÃO copia `sienge_quota_blocked_until` para
// dentro do job serializado — o campo só existe no topo do payload de status. Quem chama esta
// função a partir de um job com `error_code` de cota precisa repassar o valor de onde de fato
// veio (ver `Header.jsx`); sem ele a mensagem cai no texto-base, sem "a partir de HH:MM de dd/MM".
export const updateJobMessage = (job, quotaBlockedUntil) => {
  if (job?.error_code === SIENGE_QUOTA_ERROR_CODE) {
    return formatSiengeQuotaMessage(quotaBlockedUntil ?? job?.sienge_quota_blocked_until);
  }
  if (job?.error_code === SERVICE_RESTARTED_ERROR_CODE) {
    return SERVICE_RESTARTED_MESSAGE;
  }
  // O `error_code` sozinho não cobre tudo: uma atualização pode falhar por outro motivo
  // (ou terminar bem) tendo rotas negadas no meio. Quem decide a mensagem é o relatório
  // da extração, não só o código terminal.
  if (job?.error_code === PERMISSION_DENIED_ERROR_CODE) {
    return permissionDeniedMessage(job);
  }
  if (deniedRoutes(job).length > 0) {
    if (job?.status === "succeeded") {
      return `Atualização concluída parcialmente. ${partialExtractionMessage(job)}`;
    }
    if (["failed", "blocked"].includes(job?.status)) {
      return permissionDeniedMessage(job);
    }
  }
  switch (job?.status) {
    case "queued":
      return "Atualização na fila. Você pode sair desta página; enviaremos um e-mail quando terminar.";
    case "running":
      return "Atualização em andamento. Você pode sair desta página; enviaremos um e-mail quando terminar.";
    case "succeeded":
      return "Atualização concluída. A confirmação também é enviada por e-mail.";
    case "failed":
      return "Não foi possível concluir a atualização. Você receberá a confirmação por e-mail.";
    case "blocked":
      return "Atualização bloqueada. Você receberá a confirmação por e-mail.";
    default:
      return "";
  }
};
